import { Client } from '@twilio/conversations'

export const CLIENT_CREATION_CANCELLED = 'Twilio client creation cancelled'

const RECONNECT_BASE_DELAY_MS = 1000
const RECONNECT_MAX_DELAY_MS = 30000
const RECONNECT_MAX_ATTEMPTS = 8
const STABLE_CONNECTION_MS = 30000

export class TwilioService {
  static serviceInstance
  static conversationsClient
  static pendingClient
  static tokenProvider
  static generation = 0
  static reconnectListeners = new Set()
  static reconnectAttempts = 0
  static reconnectTimer = null

  static getInstance() {
    if (!TwilioService.serviceInstance) {
      TwilioService.serviceInstance = new TwilioService()
    }
    return TwilioService.serviceInstance
  }

  getConversationsClient = (twilioToken, getToken) => {
    if (getToken) {
      TwilioService.tokenProvider = getToken
    }
    if (TwilioService.conversationsClient) {
      return Promise.resolve(TwilioService.conversationsClient)
    }
    if (TwilioService.pendingClient) {
      return TwilioService.pendingClient
    }
    if (!twilioToken && !TwilioService.tokenProvider) {
      throw new Error('Twilio token is null or undefined')
    }

    const generation = TwilioService.generation
    const createClient = (token) =>
      Client.create(token).then((client) => {
        if (generation !== TwilioService.generation) {
          client.shutdown()
          throw new Error(CLIENT_CREATION_CANCELLED)
        }
        TwilioService.conversationsClient = client
        this.handleConnectionFailure(client)
        if (TwilioService.tokenProvider) {
          this.addTokenListener(TwilioService.tokenProvider)
        }
        return client
      })

    const pendingClient = (
      twilioToken ? createClient(twilioToken) : TwilioService.tokenProvider().then(createClient)
    ).finally(() => {
      if (TwilioService.pendingClient === pendingClient) {
        TwilioService.pendingClient = null
      }
    })
    TwilioService.pendingClient = pendingClient
    return pendingClient
  }

  // A `terminal` connection failure (expired/invalid token, a websocket drop Twilsock has given
  // up on) leaves the cached client unusable, so drop it: the next getConversationsClient() call
  // then fetches a fresh token and reconnects. Non-terminal errors are retried by the SDK itself,
  // so the client must stay cached.
  handleConnectionFailure = (client) => {
    let stableTimer = null
    const resetClient = (error) => {
      if (!(error?.terminal ?? error?.error?.terminal)) return
      console.error('Twilio chat connection error:', error)
      clearTimeout(stableTimer)
      if (TwilioService.conversationsClient === client) {
        TwilioService.conversationsClient = null
        client.shutdown()
        this.scheduleReconnect()
      }
    }
    const resetAttemptsOnceStable = (state) => {
      clearTimeout(stableTimer)
      stableTimer = null
      if (state !== 'connected') return
      stableTimer = setTimeout(() => {
        if (TwilioService.conversationsClient === client) TwilioService.reconnectAttempts = 0
      }, STABLE_CONNECTION_MS)
    }
    resetAttemptsOnceStable(client.connectionState)
    client.on('connectionStateChanged', resetAttemptsOnceStable)
    client.on('connectionError', resetClient)
    client.on('initFailed', resetClient)
  }

  // Subscribers (e.g. an open chat) hold listeners on the dropped client, so they are told when a
  // replacement is ready and re-attach to it.
  onReconnect = (listener) => {
    TwilioService.reconnectListeners.add(listener)
    return () => TwilioService.reconnectListeners.delete(listener)
  }

  // Capped exponential backoff (1s, 2s, 4s ... 30s) so a backend that keeps issuing a rejected
  // token can't turn the reconnect into a tight loop. The counter resets only once a client has
  // stayed 'connected' for STABLE_CONNECTION_MS, so connect-then-drop flapping keeps backing off, and the pending retry is cancelled by clientShutdown().
  scheduleReconnect = () => {
    if (TwilioService.reconnectTimer) return
    if (TwilioService.reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) {
      console.error('Giving up reconnecting the Twilio chat client')
      return
    }
    const delay = Math.min(
      RECONNECT_BASE_DELAY_MS * 2 ** TwilioService.reconnectAttempts,
      RECONNECT_MAX_DELAY_MS,
    )
    TwilioService.reconnectAttempts += 1
    TwilioService.reconnectTimer = setTimeout(() => {
      TwilioService.reconnectTimer = null
      this.reconnect()
    }, delay)
  }

  reconnect = () =>
    this.getConversationsClient()
      .then((client) => TwilioService.reconnectListeners.forEach((listener) => listener(client)))
      .catch((error) => {
        if (error?.message === CLIENT_CREATION_CANCELLED) return
        console.error('Failed to reconnect Twilio chat client:', error)
        this.scheduleReconnect()
      })

  clientShutdown = () => {
    TwilioService.generation += 1
    clearTimeout(TwilioService.reconnectTimer)
    TwilioService.reconnectTimer = null
    TwilioService.reconnectAttempts = 0
    if (TwilioService.conversationsClient) {
      TwilioService.conversationsClient.shutdown()
    }
    TwilioService.conversationsClient = null
    TwilioService.pendingClient = null
  }

  addTokenListener = (getToken) => {
    if (!TwilioService.conversationsClient) {
      throw new Error('Twilio client is null or undefined')
    }
    const refreshToken = () =>
      getToken()
        .then((newToken) => TwilioService.conversationsClient?.updateToken(newToken))
        .catch((error) => console.error('Failed to refresh Twilio token:', error))

    TwilioService.conversationsClient.on('tokenAboutToExpire', refreshToken)
    TwilioService.conversationsClient.on('tokenExpired', refreshToken)
    return TwilioService.conversationsClient
  }

  parseParticipants = (participants) => {
    return participants.map(this.parseParticipant)
  }

  parseParticipant = (participant) => {
    return {
      id: participant.identity,
    }
  }

  parseConversation = (conversation) => {
    return {
      id: conversation.sid,
      name: conversation.friendlyName,
      createdAt: conversation.dateCreated,
      updatedAt: conversation.dateUpdated,
      lastMessageTime: conversation.dateUpdated
        ? conversation.dateUpdated
        : conversation.dateCreated,
    }
  }

  parseMessages(messages) {
    return messages.map(this.parseMessage).reverse()
  }

  parseMessage = (message) => {
    return {
      _id: message.sid,
      text: message.body,
      createdAt: message.dateCreated,
      user: {
        _id: message.author,
      },
      isSMS: message.attributes.isSMS,
      fromAnnouncement: message.attributes.fromAnnouncement,
      messageStatus: message.attributes.status,
      duration: message.attributes.duration,
      emergency: message.attributes.emergency,
      isCall: message.attributes.isCall,
      recordingUrl: message.attributes.recordingUrl,
      transcriptionBegin: message.attributes.transcriptionBegin,
      transcriptionUrl: message.attributes.transcriptionUrl,
      received: true,
      type: message.type,
      media: message.media,
    }
  }
}
